import { useTranslation } from "react-i18next";
import Button from "../components/Button";

type SectionData = {
  title: string;
  body: [string, string];
  pills: string[];
  pdf: string;
};

export default function Governance() {
  const { t } = useTranslation();

  const sections: SectionData[] = [
    {
      title: t("governance.codeTitle"),
      body: [t("governance.codeP1"), t("governance.codeP2")],
      pills: [t("governance.codePill1"), t("governance.codePill2"), t("governance.codePill3"), t("governance.codePill4")],
      pdf: "/pdfs/code-of-conduct.pdf",
    },
    {
      title: t("governance.complianceTitle"),
      body: [t("governance.complianceP1"), t("governance.complianceP2")],
      pills: [t("governance.compliancePill1"), t("governance.compliancePill2"), t("governance.compliancePill3"), t("governance.compliancePill4")],
      pdf: "/pdfs/compliance.pdf",
    },
    {
      title: t("governance.sourcingTitle"),
      body: [t("governance.sourcingP1"), t("governance.sourcingP2")],
      pills: [t("governance.sourcingPill1"), t("governance.sourcingPill2"), t("governance.sourcingPill3"), t("governance.sourcingPill4")],
      pdf: "/pdfs/sourcing.pdf",
    },
    {
      title: t("governance.esgTitle"),
      body: [t("governance.esgP1"), t("governance.esgP2")],
      pills: [t("governance.esgPill1"), t("governance.esgPill2"), t("governance.esgPill3"), t("governance.esgPill4")],
      pdf: "/pdfs/esg.pdf",
    },
  ];

  return (
    <div className="text-white">
      {/* HERO */}
      <section className="bg-kag-bg">
        <div className="mx-auto w-full max-w-page px-5 sm:px-8 md:px-10 lg:px-[60px] pt-12 sm:pt-16 lg:pt-[85px] pb-10 sm:pb-14 lg:pb-[100px] text-center">
          <p className="font-clash text-kag-eyebrow text-[13px] sm:text-[15px] lg:text-[17px] leading-[1.2] uppercase tracking-[0.18em]">
            {t("governance.eyebrow")}
          </p>
          <h1 className="mt-5 lg:mt-7 font-clash font-medium text-white uppercase text-[56px] sm:text-[80px] md:text-[104px] lg:text-[128px] xl:text-[140px] leading-[0.98] tracking-tight">
            {t("governance.title")}
          </h1>
          <div className="mx-auto mt-6 lg:mt-9 max-w-[920px] font-clash text-[#b9b9b9] text-[14px] sm:text-[16px] lg:text-[18px] leading-[1.5]">
            <p>{t("governance.subtitle1")}</p>
            <p>{t("governance.subtitle2")}</p>
          </div>
        </div>
      </section>

      {sections.map((s) => (
        <GovernanceBlock key={s.title} s={s} />
      ))}
    </div>
  );
}

function GovernanceBlock({ s }: { s: SectionData }) {
  const { t } = useTranslation();
  return (
    <>
      {/* WHITE title band */}
      <section className="bg-white text-black">
        <div className="mx-auto w-full max-w-page px-5 sm:px-8 md:px-10 lg:px-[60px] py-8 sm:py-10 lg:py-[48px]">
          <div className="flex items-start gap-3 lg:gap-5">
            <span className="block bg-kag-red rounded-full mt-2 lg:mt-3 shrink-0 w-[14px] h-[14px] lg:w-[18px] lg:h-[18px]" />
            <h2 className="font-clash font-semibold text-[#010f1f] text-[24px] sm:text-[30px] md:text-[36px] lg:text-[44px] leading-[1.05] tracking-tight">
              {s.title}
            </h2>
          </div>
        </div>
      </section>

      {/* DARK content */}
      <section className="bg-kag-bg">
        <div className="mx-auto w-full max-w-page px-5 sm:px-8 md:px-10 lg:px-[42px] py-10 md:py-14 lg:py-[80px]">
          <div className="grid gap-10 lg:grid-cols-2 lg:gap-[60px]">
            <div>
              <div className="space-y-4 font-clash font-medium text-white text-[15px] sm:text-[16px] lg:text-[17px] leading-[1.55] max-w-[640px]">
                <p>{s.body[0]}</p>
                <p>{s.body[1]}</p>
              </div>
              <a href={s.pdf} download className="inline-block mt-7 lg:mt-9">
                <Button variant="filled">
                  {t("common.downloadPdf")}
                </Button>
              </a>
            </div>
            <div className="flex flex-col gap-3 lg:gap-4 lg:max-w-[520px] lg:ml-auto w-full">
              {s.pills.map((p) => (
                <Pill key={p}>{p}</Pill>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function Pill({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="flex items-center gap-3 lg:gap-4 border-y border-l border-white/95 rounded-full px-5 sm:px-7 lg:px-10 py-3 sm:py-4 lg:py-[18px]"
      style={{ borderRightWidth: 0, borderWidth: 1 }}
    >
      <span className="block bg-kag-red rounded-full shrink-0 w-[8px] h-[8px] lg:w-[9px] lg:h-[9px]" />
      <p className="font-clash font-medium text-white text-[13px] sm:text-[14px] lg:text-[16px] leading-[1.3] tracking-tight">
        {children}
      </p>
    </div>
  );
}
